import React from 'react';
import { CalendarDays, CreditCard, User } from 'lucide-react';
import { cn } from '../../lib/utils';

export type GuestReservationWizardStep = 1 | 2 | 3;

type StepDef = {
  id: GuestReservationWizardStep;
  label: string;
  icon: React.ReactNode;
};

type GuestReservationStepTrackerProps = {
  step: GuestReservationWizardStep;
  step1Done: boolean;
  step2Done: boolean;
  step3Done: boolean;
  labels: [string, string, string];
  onSelectStep?: (step: GuestReservationWizardStep) => void;
};

/**
 * 3-step tracker for quick reservation:
 * 1 Dates+Groups+Note → 2 Basic info → 3 Hear about us / promo / pay
 */
export const GuestReservationStepTracker: React.FC<GuestReservationStepTrackerProps> = ({
  step,
  step1Done,
  step2Done,
  step3Done,
  labels,
  onSelectStep,
}) => {
  const steps: StepDef[] = [
    { id: 1, label: labels[0], icon: <CalendarDays className="h-3 w-3" aria-hidden /> },
    { id: 2, label: labels[1], icon: <User className="h-3 w-3" aria-hidden /> },
    { id: 3, label: labels[2], icon: <CreditCard className="h-3 w-3" aria-hidden /> },
  ];
  const done = { 1: step1Done, 2: step2Done, 3: step3Done } as const;

  return (
    <ol className="flex w-full items-stretch gap-1 rounded-xl border border-[#3d2817]/10 bg-white/90 p-1.5 sm:gap-2 sm:p-2">
      {steps.map((s, i) => (
        <React.Fragment key={s.id}>
          {i > 0 ? (
            <li className="flex w-2 shrink-0 items-center justify-center sm:w-3" aria-hidden>
              <span className="h-px w-full bg-[#3d2817]/15" />
            </li>
          ) : null}
          <li className="min-w-0 flex-1">
            <button
              type="button"
              onClick={() => onSelectStep?.(s.id)}
              className={cn(
                'flex w-full items-center gap-1.5 rounded-lg px-1.5 py-2 text-left transition-colors sm:gap-2 sm:px-2.5',
                step === s.id ? 'bg-[#2f9e44]/12 ring-1 ring-[#2f9e44]/35' : 'hover:bg-black/[0.03]',
              )}
            >
              <span
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold sm:h-7 sm:w-7 sm:text-xs',
                  done[s.id]
                    ? 'bg-[#2f9e44] text-white'
                    : step === s.id
                      ? 'bg-[#3d2817] text-white'
                      : 'bg-[#3d2817]/10 text-[#3d2817]',
                )}
              >
                {done[s.id] ? '✓' : s.id}
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-[#3d2817]/55 sm:text-[10px]">
                  {s.icon}
                  <span className="hidden sm:inline">Step {s.id}</span>
                </span>
                <span className="block truncate text-[11px] font-semibold text-[#3d2817] sm:text-xs">
                  {s.label}
                </span>
              </span>
            </button>
          </li>
        </React.Fragment>
      ))}
    </ol>
  );
};

export default GuestReservationStepTracker;
